import TanStackQueryDevtools from "@/components/devtools/query";
import TanStackRouterDevtools from "@/components/devtools/router";
import env from "@/env.config";
import cookie from "@/libraries/cookie";
import { memberProfileQueryOptions, queryClient } from "@/queries";
import { RootRouteSearchSchema } from "@/schemas";
import type { RootRouteSearchSchemaType } from "@/schemas";
import { authService } from "@/services";
import { useDownloadShallow } from "@/stores/download";
import * as Sentry from "@sentry/react";
import {
	Outlet,
	createRootRouteWithContext,
	redirect,
	useRouterState,
} from "@tanstack/react-router";
import type { RouteContext } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { Suspense, lazy, useEffect, useLayoutEffect, useMemo } from "react";
import { isMobile } from "react-device-detect";
import NotFound from "./-components/NotFound";

const DownloadMobile = lazy(() => import("@/components/alert/DownloadMobile"));

export const Route = createRootRouteWithContext<RouteContext>()({
	validateSearch: zodSearchValidator(RootRouteSearchSchema),
	beforeLoad: async ({ context, search }) => {
		const searchParams = search as RootRouteSearchSchemaType;
		const searchToken = searchParams.token;
		const accessToken =
			searchParams["access-token"] && searchParams.client && searchParams.uid;
		if (searchToken || accessToken) {
			try {
				await authService.clearToken();
				const token = searchToken
					? JSON.parse(atob(searchToken as string))
					: (searchParams ?? {});
				const accessToken = token["access-token"] ?? token.access_token;
				if (!accessToken) return;
				await authService.setToken({
					"access-token": accessToken,
					client: token.client,
					uid: token.uid,
				});
				if (token["access-token"]) {
					cookie.set("account-type", "mpis");
				}
				await context.queryClient.cancelQueries();
				context.queryClient.clear();
				const resProfile = await queryClient.fetchQuery(
					memberProfileQueryOptions(),
				);
				Sentry.isEnabled() &&
					Sentry.setUser({
						id: resProfile.user.id,
						email: token.uid,
						...(resProfile.user.fullname
							? { username: resProfile.user.fullname }
							: {}),
					});
				if (search.encounter_id) {
					if (!search.have_cases) {
						return redirect({
							replace: true,
							to: "/dashboard/case/create",
							search: {
								step: 2,
								encounter_id: search.encounter_id,
							},
						});
					}
					if (search.patient_ic_number) {
						return redirect({
							replace: true,
							to: "/dashboard/case/create/exist/patient/$patientIc",
							params: {
								patientIc: search.patient_ic_number,
							},
							search: {
								have_cases: search.have_cases,
								encounter_id: search.encounter_id,
								reference: "/",
							},
						});
					}
				}
			} catch (error) {
				localStorage.removeItem(env.webviewKey);
			}
		}
	},
	component: RootComponent,
	notFoundComponent: NotFound,
});

function RootComponent() {
	const isPopupOpen = useDownloadShallow((state) => state.isPopupOpen);
	const showPopup = useDownloadShallow((state) => state.showPopup);
	const shouldShowPopup = useDownloadShallow((state) => state.shouldShowPopup);
	const isPending = useRouterState({
		select: (s) => s.status === "pending",
	});
	const searchIsMobile = Route.useSearch({
		select: (state) => state?.is_mobile,
	});

	const isOpenDownload = useMemo(
		() => !isPending && isPopupOpen && isMobile,
		[isPending, isPopupOpen],
	);

	useEffect(() => {
		if (searchIsMobile) {
			localStorage.setItem(env.webviewKey, String(searchIsMobile));
		}
	}, [searchIsMobile]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useLayoutEffect(() => {
		if (shouldShowPopup()) showPopup();
	}, []);

	return (
		<>
			<div id="wrapper" className="relative wrapper h-dvh flex flex-col">
				<Suspense>
					{isOpenDownload ? <DownloadMobile position="relative" /> : null}
				</Suspense>
				<div className="flex-1 overflow-y-auto">
					<Outlet />
				</div>
			</div>
			<Suspense>
				<TanStackQueryDevtools position="right" />
				<TanStackRouterDevtools position="bottom-left" />
			</Suspense>
		</>
	);
}
